import { Home, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { LocalizedLink } from '@/components/LocalizedLink'

interface HeroCta {
  label: string
  href: string
  variant: 'primary' | 'secondary'
  external?: boolean
}

interface HeroSimpleProps {
  backgroundColor?: string
  textColor?: string
  logoSrc?: string
  logoAlt?: string
  icon?: LucideIcon
  title: string
  subtitle?: string
  ctas?: HeroCta[]
  className?: string
}

export function HeroSimple({
  backgroundColor = 'bg-maia',
  textColor = 'text-white',
  logoSrc,
  logoAlt,
  icon: Icon,
  title,
  subtitle,
  ctas,
  className,
}: HeroSimpleProps) {
  const isExternal = (href: string) =>
    href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:')

  const renderIcon = () => {
    if (logoSrc) {
      return (
        <img
          src={logoSrc}
          alt={logoAlt || ''}
          className="mb-6 h-24 w-auto rounded-2xl bg-white/10 object-contain p-3 md:h-32"
          width="128"
          height="128"
        />
      )
    }
    const IconComponent = Icon || Home
    return (
      <IconComponent
        className="mb-6 h-16 w-16 opacity-90 md:h-20 md:w-20"
        strokeWidth={1}
        aria-hidden="true"
      />
    )
  }

  return (
    <section
      className={cn(
        'relative flex min-h-[60vh] flex-col items-center justify-center px-4 py-24 text-center md:min-h-[70vh] md:px-6 lg:px-8',
        backgroundColor,
        textColor,
        className
      )}
    >
      {renderIcon()}
      <h1 className="max-w-4xl text-[clamp(2.5rem,6vw,4.5rem)] font-extrabold leading-[1.1] tracking-tight">
        {title}
      </h1>
      {subtitle && (
        <p className="mt-5 max-w-2xl text-lg font-medium leading-relaxed opacity-95 md:text-xl">
          {subtitle}
        </p>
      )}
      {ctas && ctas.length > 0 && (
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          {ctas.map((cta, index) => {
            const baseClasses =
              'inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 active:scale-[0.98]'
            const variantClasses =
              cta.variant === 'primary'
                ? 'bg-white text-maia hover:bg-cream'
                : 'border-2 border-white/80 text-white hover:bg-white/10'
            const classes = cn(baseClasses, variantClasses)

            if (cta.external || isExternal(cta.href)) {
              return (
                <a
                  key={index}
                  href={cta.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes}
                >
                  {cta.label}
                </a>
              )
            }

            if (cta.href.startsWith('#')) {
              return (
                <a key={index} href={cta.href} className={classes}>
                  {cta.label}
                </a>
              )
            }

            return (
              <LocalizedLink key={index} to={cta.href} className={classes}>
                {cta.label}
              </LocalizedLink>
            )
          })}
        </div>
      )}
    </section>
  )
}
